import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, ChevronDown, PlayCircle, BookOpen, Award, Users, Wrench, CreditCard, MessageSquare } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Badge } from '../components/ui/Badge';
const helpCategories = [{
  id: 'getting-started',
  title: 'Getting Started',
  icon: PlayCircle,
  faqs: [{
    q: 'How do I enrol in a course?',
    a: 'Simply navigate to the Courses page, select your desired course, and click "Enrol Now". You will be guided through our secure checkout process.'
  }, {
    q: 'What courses do you offer?',
    a: 'We offer a range of FSAI-compliant food safety courses including HACCP Levels 1-3, Allergen Awareness, and Food Safety Management.'
  }, {
    q: 'Do I need any prerequisites?',
    a: 'Most of our courses, including HACCP Level 1 & 2, do not require prior knowledge. You just need a good understanding of English and a stable internet connection.'
  }]
}, {
  id: 'course-access',
  title: 'Course Access',
  icon: BookOpen,
  faqs: [{
    q: 'How do I access my course?',
    a: 'After enrolment, you will receive an email with login details. You can access your course immediately via our online learning portal.'
  }, {
    q: 'Can I pause and resume?',
    a: 'Yes! Our platform automatically saves your progress. You can log out and resume exactly where you left off on any device.'
  }, {
    q: 'How long do I have access?',
    a: 'You have 6 months of unlimited access to complete your course from the date of enrolment.'
  }]
}, {
  id: 'certificates',
  title: 'Certificates',
  icon: Award,
  faqs: [{
    q: 'When do I get my certificate?',
    a: 'Your CPD-accredited certificate is generated instantly upon achieving the required 70% pass mark in the final assessment.'
  }, {
    q: 'How do I download it?',
    a: 'You can download your certificate as a high-quality PDF directly from your dashboard at any time.'
  }, {
    q: 'Can I order a hard copy?',
    a: 'Yes, during checkout or from your dashboard, you can opt to have a premium hard copy certificate posted to your address.'
  }]
}, {
  id: 'group-bookings',
  title: 'Group Bookings',
  icon: Users,
  faqs: [{
    q: 'How do I book for my team?',
    a: 'Visit our Corporate Training page or contact us directly. We can set up a manager account for you to enrol multiple staff members.'
  }, {
    q: 'Is there a manager dashboard?',
    a: 'Yes, group bookings include access to a dedicated dashboard where you can assign courses, track progress, and download team certificates.'
  }, {
    q: 'What are volume discounts?',
    a: 'We offer tiered pricing discounts for teams of 5 or more. Contact our corporate team for a custom quote.'
  }]
}, {
  id: 'technical-support',
  title: 'Technical Support',
  icon: Wrench,
  faqs: [{
    q: 'What devices are supported?',
    a: 'Our platform is fully responsive and works on all modern desktop browsers, tablets, and smartphones.'
  }, {
    q: "I can't log in",
    a: 'Please use the "Forgot Password" link on the login page. If you still have issues, contact our support team.'
  }, {
    q: "Video won't play",
    a: 'Ensure you have a stable internet connection. Try clearing your browser cache or accessing the course in an incognito/private window.'
  }]
}, {
  id: 'billing',
  title: 'Billing & Payments',
  icon: CreditCard,
  faqs: [{
    q: 'What payment methods are accepted?',
    a: 'We accept all major credit and debit cards securely via Stripe. Corporate clients can also pay via invoice.'
  }, {
    q: 'Can I get a refund?',
    a: 'We offer a 14-day money-back guarantee provided you have not completed more than 20% of the course.'
  }, {
    q: 'Do you provide invoices?',
    a: 'Yes, a VAT receipt/invoice is automatically emailed to you upon purchase and can be downloaded from your account.'
  }]
}];
function FaqAccordion({
  faqs
}: {
  faqs: {
    q: string;
    a: string;
  }[];
}) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  return <div className="space-y-3" data-id="element-783">
      {faqs.map((faq, i) => <div key={i} className="bg-white border-2 border-forest-100 rounded-2xl overflow-hidden" data-id="element-784">
          <button onClick={() => setOpenIndex(openIndex === i ? null : i)} className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-forest-50/50 transition-colors" data-id="element-785">
            <span className="font-bold text-forest-900 pr-4" data-id="element-786">{faq.q}</span>
            <ChevronDown className={`w-5 h-5 text-forest-500 shrink-0 transition-transform duration-300 ${openIndex === i ? 'rotate-180' : ''}`} data-id="element-787" />
          </button>
          <AnimatePresence data-id="element-788">
            {openIndex === i && <motion.div initial={{
          height: 0,
          opacity: 0
        }} animate={{
          height: 'auto',
          opacity: 1
        }} exit={{
          height: 0,
          opacity: 0
        }} transition={{
          duration: 0.3
        }} className="overflow-hidden" data-id="element-789">
                <div className="px-6 py-4 bg-forest-50/30 border-t border-forest-100 text-sm text-gray-600 leading-relaxed" data-id="element-790">
                  {faq.a}
                </div>
              </motion.div>}
          </AnimatePresence>
        </div>)}
    </div>;
}
export function HelpCentrePage() {
  const [searchQuery, setSearchQuery] = useState('');
  return <div className="min-h-screen bg-cream-50 pt-20 pb-24" data-id="element-791">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-forest-900 via-forest-800 to-forest-950 text-white overflow-hidden py-24 mb-16" data-id="element-792">
        <div className="absolute inset-0 bg-mesh opacity-30" data-id="element-793"></div>
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-ember-500/20 rounded-full blur-3xl" data-id="element-794"></div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center" data-id="element-795">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} data-id="element-796">
            <h1 className="text-4xl md:text-6xl font-display font-bold mb-6" data-id="element-797">
              How can we <span className="text-ember-400" data-id="element-798">help you?</span>
            </h1>

            <div className="relative max-w-2xl mx-auto mt-10" data-id="element-799">
              <Search className="absolute left-5 top-1/2 -translate-y-1/2 w-6 h-6 text-gray-400" data-id="element-800" />
              <input type="text" placeholder="Search for answers..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-14 pr-6 py-5 rounded-full border-0 focus:ring-4 focus:ring-ember-500/30 text-lg text-gray-900 shadow-2xl" data-id="element-801" />
            </div>
          </motion.div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-802">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16" data-id="element-803">
          {helpCategories.map((category, index) => <motion.div key={category.id} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: index * 0.1
        }} className="bg-white rounded-3xl p-8 border-2 border-forest-100 shadow-lg" data-id="element-804">
              <div className="w-14 h-14 bg-gradient-to-br from-forest-50 to-cream-100 rounded-2xl flex items-center justify-center mb-6 border border-forest-100" data-id="element-805">
                <category.icon className="w-7 h-7 text-forest-600" data-id="element-806" />
              </div>
              <h2 className="text-2xl font-display font-bold text-forest-900 mb-6" data-id="element-807">
                {category.title}
              </h2>
              <FaqAccordion faqs={category.faqs} data-id="element-808" />
            </motion.div>)}
        </div>

        {/* Contact CTA */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="bg-gradient-to-br from-forest-900 to-forest-800 rounded-3xl p-12 text-center text-white relative overflow-hidden" data-id="element-809">
          <div className="absolute inset-0 bg-mesh opacity-20" data-id="element-810"></div>
          <div className="relative z-10" data-id="element-811">
            <div className="w-16 h-16 bg-ember-500 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg" data-id="element-812">
              <MessageSquare className="w-8 h-8 text-white" data-id="element-813" />
            </div>
            <h2 className="text-3xl font-display font-bold mb-4" data-id="element-814">
              Still need help?
            </h2>
            <p className="text-forest-200 text-lg mb-8 max-w-2xl mx-auto" data-id="element-815">
              Can't find the answer you're looking for? Our support team is
              ready to assist you with any questions.
            </p>
            <Link to="/contact" className="inline-flex items-center justify-center px-8 py-4 text-lg font-bold rounded-full bg-white text-forest-900 hover:bg-forest-50 transition-colors shadow-lg hover:shadow-xl hover:-translate-y-1" data-id="element-816">
              Contact Support
            </Link>
          </div>
        </motion.div>
      </div>
    </div>;
}